/**
 * `SequenceHoldBanner` — the stakes banner every board shows while a chained resolution is held
 * before a high-stakes roll (`chained-resolution-sequence` 3.2, `decisions/0210` items 3-6).
 *
 * The board has paused right before the roll (the Elite Cataphract's structure check). Every screen
 * reads what is at stake; the GM and the controller of the unit making the roll get **Roll it**,
 * everyone else reads who the table is waiting on. The dice were drawn when the resolution
 * committed, so nothing here decides anything: pressing Roll it opens every board's latch and the
 * held die tumbles. The GM does not see the outcome unless they choose to peek (item 6).
 */
import { useState } from "react";
import type { HoldView } from "./presentationDirector";
import "./sequence-hold.css";

export function SequenceHoldBanner({
  hold,
  canRelease,
  isGm,
  rollerName,
  onRelease,
}: {
  hold: HoldView;
  /** The viewer is the GM or controls the unit making the roll. */
  canRelease: boolean;
  isGm: boolean;
  /** The rolling unit's display name, for the waiting line. */
  rollerName: string;
  onRelease: () => void;
}) {
  const [peek, setPeek] = useState(false);
  const [sent, setSent] = useState(false);
  return (
    <div className="seq-hold" role="status" aria-live="polite" data-hold-key={hold.key}>
      <span className="seq-hold-tag">HOLD</span>
      <span className="seq-hold-stakes">{hold.stakes}</span>
      {isGm && hold.outcome !== undefined && (
        <button type="button" className="seq-hold-peek" aria-pressed={peek} onClick={() => setPeek((p) => !p)}>
          {peek ? `→ ${hold.outcome}` : "Peek"}
        </button>
      )}
      {canRelease ? (
        <button
          type="button"
          className="seq-hold-roll"
          disabled={sent}
          onClick={() => {
            setSent(true);
            onRelease();
          }}
        >
          Roll it
        </button>
      ) : (
        <span className="seq-hold-wait">Waiting on the GM or {rollerName}'s player</span>
      )}
    </div>
  );
}
